import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { FormsModule } from '@angular/forms';
import { UserService, TransactionFilters } from '../../../service/user.service';
import { MessageService, ConfirmationService } from 'primeng/api';

export type TransactionFilterType = 'all' | 'Pending' | 'Approved' | 'Rejected';

interface Transaction {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    fullName: string;
    id: string;
  };
  portfolioId: {
    _id: string;
    name: string;
    ticketPrice: number;
    availableTickets: number;
  };
  amount: number;
  ticketsPurchased: number;
  transactionType: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface TransactionResponse {
  success: boolean;
  data: {
    transactions: Transaction[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

@Component({
  selector: 'app-transactions-tab',
  standalone: true,
  imports: [CommonModule, LucideIconsModule, PrimengImports, FormsModule],
  templateUrl: './transactions-tab.component.html',
  styleUrl: './transactions-tab.component.css',
  providers: [MessageService, ConfirmationService]
})
export class TransactionsTabComponent implements OnInit {
  transactions: Transaction[] = [];
  
  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  
  // Loading states
  isLoading: boolean = false;
  error: any;
  
  // Filters
  selectedFilter: TransactionFilterType = 'all';
  
  // User authentication
  userId: string | null = null;
  isAuthenticated: boolean = false;

  filterOptions = [
    { value: 'all' as TransactionFilterType, label: 'All Transactions', icon: 'list' },
    { value: 'Pending' as TransactionFilterType, label: 'Pending', icon: 'clock' },
    { value: 'Approved' as TransactionFilterType, label: 'Approved', icon: 'check-circle' },
    { value: 'Rejected' as TransactionFilterType, label: 'Rejected', icon: 'circle-x' }
  ];

  // PrimeNG Table settings
  rows: number = 10;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.initializeUserId();
    if (this.isAuthenticated && this.userId) {
      this.loadTransactions();
    }
  }

  private initializeUserId(): void {
    try {
      this.isAuthenticated = this.userService.isAuthenticated();
      
      if (!this.isAuthenticated) {
        this.error = 'Please log in to view your transactions';
        this.messageService.add({
          severity: 'warn',
          summary: 'Authentication Required',
          detail: 'Please log in to view your transactions'
        });
        return;
      }

      this.userId = this.userService.getUserIdFromToken();

      if (!this.userId) {
        this.error = 'User ID not found in authentication token';
        this.isAuthenticated = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Authentication Error',
          detail: 'User ID not found in authentication token'
        });
        return;
      }

    } catch (error) {
      console.error('Error initializing user ID:', error);
      this.error = 'Failed to authenticate user';
      this.isAuthenticated = false;
      this.userId = '';
      this.messageService.add({
        severity: 'error',
        summary: 'Authentication Failed',
        detail: 'Failed to authenticate user'
      });
    }
  }

  loadTransactions(): void {
    if (!this.userId || !this.isAuthenticated) {
      this.error = 'User not authenticated';
      return;
    }

    this.isLoading = true;
    this.error = null;

    const filters: TransactionFilters = {
      userId: this.userId,
      page: this.currentPage,
      limit: this.itemsPerPage
    };

    // Add status filter if not 'all'
    if (this.selectedFilter !== 'all') {
      filters.status = this.selectedFilter as 'Pending' | 'Approved' | 'Rejected';
    }

    this.userService.getUserTransactions(filters).subscribe({
      next: (response: TransactionResponse) => {
        if (response.success) {
          this.transactions = response.data.transactions;
          this.totalItems = response.data.pagination.total;
          this.totalPages = response.data.pagination.totalPages;
          
          if (this.transactions.length === 0 && this.selectedFilter !== 'all') {
            this.messageService.add({
              severity: 'info',
              summary: 'No Results',
              detail: `No ${this.selectedFilter.toLowerCase()} transactions found`
            });
          }
        } else {
          this.error = 'Failed to load transactions';
          this.messageService.add({
            severity: 'error',
            summary: 'Load Failed',
            detail: 'Failed to load transactions'
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading transactions:', error);
        
        if (error.status === 401) {
          this.error = 'Session expired. Please log in again.';
          this.isAuthenticated = false;
          this.messageService.add({
            severity: 'warn',
            summary: 'Session Expired',
            detail: 'Please log in again'
          });
        } else if (error.status === 404) {
          this.error = 'No transactions found.';
          this.transactions = [];
          this.messageService.add({
            severity: 'info',
            summary: 'No Transactions',
            detail: 'No transactions found for your account'
          });
        } else {
          this.error = error.message || 'Failed to load transactions. Please try again.';
          this.messageService.add({
            severity: 'error',
            summary: 'Error Loading Transactions',
            detail: this.error
          });
        }
        this.isLoading = false;
      }
    });
  }


  // PrimeNG Pagination handler
  onPageChange(event: any): void {
    this.currentPage = event.page + 1; // PrimeNG uses 0-based indexing
    this.itemsPerPage = event.rows;
    this.loadTransactions();
  }

  // Export functionality
  onExport(): void {
    if (this.transactions.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No transactions to export'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Export ${this.transactions.length} transactions to CSV?`,
      header: 'Export Confirmation',
      icon: 'pi pi-download',
      acceptLabel: 'Export',
      rejectLabel: 'Cancel',
      accept: () => {
        const csvContent = this.generateCSV();
        this.downloadCSV(csvContent, `transactions_${this.selectedFilter}_${new Date().toISOString().split('T')[0]}.csv`);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Export Successful',
          detail: 'Transactions exported successfully'
        });
      }
    });
  }

  private generateCSV(): string {
    const headers = ['Transaction ID', 'Portfolio', 'Amount (AED)', 'Tickets', 'Status', 'Date', 'Type'];
    const rows = this.transactions.map(transaction => [
      transaction._id,
      transaction.portfolioId?.name || 'N/A',
      transaction.amount.toString(),
      transaction.ticketsPurchased.toString(),
      transaction.status,
      this.formatDate(transaction.createdAt),
      transaction.transactionType
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }

  private downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  // Utility methods
  formatDate(dateString: string): string {
    if (!dateString) {
      return ' - ';
    }

    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getStatusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      case 'cancelled':
        return 'danger';
      default:
        return 'info';
    }
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'clock';
      case 'approved':
        return 'check-circle';
      case 'rejected':
        return 'circle-x';
      case 'cancelled':
        return 'circle-x';
      default:
        return 'circle-question-mark';
    }
  }

  refreshTransactions(): void {
    if (this.isAuthenticated && this.userId) {
      this.messageService.add({
        severity: 'info',
        summary: 'Refreshing',
        detail: 'Loading latest transactions...'
      });
      this.loadTransactions();
    } else {
      this.initializeUserId();
      if (this.isAuthenticated && this.userId) {
        this.loadTransactions();
      }
    }
  }


  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Transaction ID copied to clipboard'
        });
      }).catch(err => {
        console.error('Failed to copy: ', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Copy Failed',
          detail: 'Failed to copy transaction ID'
        });
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Transaction ID copied to clipboard'
        });
      } catch (err) {
        console.error('Failed to copy: ', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Copy Failed',
          detail: 'Failed to copy transaction ID'
        });
      }
      document.body.removeChild(textArea);
    }
  }

  /**
   * Cancel a pending transaction
   * @param transactionId The ID of the transaction to cancel
   * @param event The click event to prevent default behavior
   */
  cancelTransaction(transactionId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to cancel this transaction? This action cannot be undone.',
      header: 'Confirm Cancellation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'bg-gradient-to-r from-amber-500 to-amber-600 border-amber-500 text-white',
      rejectButtonStyleClass: 'border-gray-300 text-gray-700',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.isLoading = true;
        this.userService.cancelTransaction(transactionId).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: response.message || 'Transaction cancelled successfully',
                life: 5000
              });
              // Refresh the transactions list
              this.loadTransactions();
            } else {
              this.isLoading = false;
              this.messageService.add({
                severity: 'warn',
                summary: 'Cancellation Failed',
                detail: response.message || 'Failed to cancel transaction',
                life: 5000
              });
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error cancelling transaction:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.message || 'An error occurred while cancelling the transaction',
              life: 5000
            });
          },
          complete: () => {
            this.isLoading = false;
          }
        });
      },
      reject: () => {
        // User cancelled the confirmation
      }
    });
  }
}
